import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useMemo } from 'react'
import { BarChart3, Calendar, Sparkles, ExternalLink, FileText } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { api } from '@/services/api'
import { TICKET_PERIODS, TicketTimeTracking, type TicketPeriod } from '@/components/analytics/TicketTimeTracking'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { AllProjectStatusLabels } from '@/utils/enum'

export const Route = createFileRoute('/projects/dashboard')({
  component: DashboardAnalytics,
})

interface ProjectDashboard {
  id: number
  nom: string
  status: string
  client_nom: string | null
  activity_sector_nom: string | null
  montant_facture_build?: number | null
  montant_run?: number | null
  couts?: number | null
  jours_vendu?: number | null
  jours_reel?: number | null
}

interface WorkshopURL {
  id: number
  url_type: string
  url: string
  created_at: string
}

interface WorkshopDashboard {
  id: number
  description: string | null
  date: string
  consultants: string | null
  prix: number | null
  client_id: string
  activity_sector_id: number | null
  created_at: string
  updated_at: string | null
  urls: WorkshopURL[]
  client_nom: string | null
  activity_sector_nom: string | null
}

function median(arr: number[]): number {
  if (arr.length === 0) return 0
  const s = [...arr].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2
}

function fmtEuro(v: number): string {
  return `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`
}

function DashboardAnalytics() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isSuperAdmin = user?.role === 'super_admin'

  const [projects, setProjects] = useState<ProjectDashboard[]>([])
  const [workshops, setWorkshops] = useState<WorkshopDashboard[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingWorkshops, setLoadingWorkshops] = useState(true)
  
  // Les dates sont vides par défaut
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [analyticsView, setAnalyticsView] = useState<'overview' | 'tickets'>('overview')
  const [ticketPeriod, setTicketPeriod] = useState<TicketPeriod>('all')

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate({ to: '/projects' })
      return
    }
    const fetchData = async () => {
      setLoading(true)
      try {
        const params: Record<string, string> = {}
        if (startDate) {
          params.date_kickoff_from = startDate
        }
        if (endDate) {
          params.date_kickoff_to = endDate
        }
        const res = await api.get<{ data: ProjectDashboard[]; count: number }>('/projects', {
          ...params,
          for_analytics: 'true',
        })
        setProjects(res.data?.data || [])
      } catch (e) {
        console.error('Erreur chargement dashboard:', e)
        setProjects([])
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [isSuperAdmin, navigate, startDate, endDate])

  useEffect(() => {
    if (!isSuperAdmin) {
      return
    }
    const fetchWorkshops = async () => {
      setLoadingWorkshops(true)
      try {
        const res = await api.get<{ data: WorkshopDashboard[]; count: number }>('/workshops')
        let workshopsData = res.data?.data || []
        
        // Filter workshops by date on frontend (since backend doesn't support it yet)
        if (startDate || endDate) {
          workshopsData = workshopsData.filter((w) => {
            const workshopDate = new Date(w.date)
            if (startDate && workshopDate < new Date(startDate)) {
              return false
            }
            if (endDate && workshopDate > new Date(endDate)) {
              return false
            }
            return true
          })
        }
        
        setWorkshops(workshopsData)
      } catch (e) {
        console.error('Erreur chargement workshops:', e)
        setWorkshops([])
      } finally {
        setLoadingWorkshops(false)
      }
    }
    fetchWorkshops()
  }, [isSuperAdmin, startDate, endDate])

  const builds = useMemo(() => projects.map(p => p.montant_facture_build).filter((v): v is number => v != null && v > 0), [projects])
  const runs = useMemo(() => projects.map(p => p.montant_run).filter((v): v is number => v != null && v >= 0), [projects])
  // Industries combinées (projets + workshops)
  const industries = useMemo(() => {
    const set = new Set<string>()
    projects.forEach(p => { if (p.activity_sector_nom) set.add(p.activity_sector_nom!) })
    workshops.forEach(w => { if (w.activity_sector_nom) set.add(w.activity_sector_nom!) })
    return set.size
  }, [projects, workshops])

  const byIndustry = useMemo(() => {
    const m = new Map<string, number>()
    projects.forEach(p => {
      const n = p.activity_sector_nom || 'Non renseigné'
      m.set(n, (m.get(n) || 0) + 1)
    })
    return Array.from(m.entries()).map(([nom, count]) => ({ nom, count }))
  }, [projects])

  const byClient = useMemo(() => {
    const m = new Map<string, number>()
    projects.forEach(p => {
      const n = p.client_nom || 'Non renseigné'
      m.set(n, (m.get(n) || 0) + 1)
    })
    return Array.from(m.entries()).map(([nom, count]) => ({ nom, count }))
  }, [projects])

  // Workshop statistics
  const workshopPrices = useMemo(() => workshops.map(w => w.prix).filter((v): v is number => v != null && v > 0), [workshops])

  const workshopsByIndustry = useMemo(() => {
    const m = new Map<string, number>()
    workshops.forEach(w => {
      const n = w.activity_sector_nom || 'Non renseigné'
      m.set(n, (m.get(n) || 0) + 1)
    })
    return Array.from(m.entries()).map(([nom, count]) => ({ nom, count }))
  }, [workshops])

  const workshopsByClient = useMemo(() => {
    const m = new Map<string, number>()
    workshops.forEach(w => {
      const n = w.client_nom || 'Non renseigné'
      m.set(n, (m.get(n) || 0) + 1)
    })
    return Array.from(m.entries()).map(([nom, count]) => ({ nom, count }))
  }, [workshops])

  const prixMoyen = workshopPrices.length ? workshopPrices.reduce((a, b) => a + b, 0) / workshopPrices.length : 0
  const prixMediane = workshopPrices.length ? median(workshopPrices) : 0

  const buildMoyen = builds.length ? builds.reduce((a, b) => a + b, 0) / builds.length : 0
  const buildMediane = builds.length ? median(builds) : 0
  const runMoyen = runs.length ? runs.reduce((a, b) => a + b, 0) / runs.length : 0
  const runMediane = runs.length ? median(runs) : 0

  // Calcul des totaux pour la ligne TOTAL
  const totals = useMemo(() => {
    const totalBuild = projects.reduce((sum, p) => sum + (p.montant_facture_build || 0), 0)
    const totalRun = projects.reduce((sum, p) => sum + (p.montant_run || 0), 0)
    const totalCouts = projects.reduce((sum, p) => sum + (p.couts || 0), 0)
    const totalMarge = totalBuild > 0 ? ((totalBuild - totalCouts) / totalBuild * 100) : null
    const ecarts = projects
      .map(p => (p.jours_reel != null && p.jours_vendu != null) ? (p.jours_reel - p.jours_vendu) : null)
      .filter((e): e is number => e != null)
    const moyenneEcart = ecarts.length > 0 ? ecarts.reduce((a, b) => a + b, 0) / ecarts.length : null
    
    return {
      build: totalBuild,
      run: totalRun,
      couts: totalCouts,
      marge: totalMarge,
      ecart: moyenneEcart
    }
  }, [projects])

  const getStatusLabel = (s: string) => AllProjectStatusLabels[s] || s
  const getStatusColor = (s: string) => {
    const v = (s || '').toLowerCase()
    if (v === 'delivered' || v === 'completed') return 'bg-green-100 text-green-700 border-green-200'
    if (v === 'in_development' || v === 'in_progress') return 'bg-blue-100 text-blue-700 border-blue-200'
    if (v === 'maintenance' || v === 'on_hold') return 'bg-amber-100 text-amber-700 border-amber-200'
    if (v === 'pre_development' || v === 'planning') return 'bg-slate-100 text-slate-700 border-slate-200'
    if (v === 'cancelled') return 'bg-red-100 text-red-700 border-red-200'
    if (v === 'draft') return 'bg-gray-100 text-gray-600 border-gray-200'
    return 'bg-gray-100 text-gray-700 border-gray-200'
  }
  const getEcartColor = (ecart: number) => {
    if (ecart < 0) return 'text-green-600'      // -1 à -x: dans les temps
    if (ecart <= 10) return 'text-[#D08700]'    // 0-10
    if (ecart <= 14) return 'text-orange-600'   // 11-14
    return 'text-red-600'                         // 15+
  }

  // Format date for workshops (e.g., "15 déc. 2024")
  const formatWorkshopDate = (dateString: string): string => {
    const date = new Date(dateString)
    const day = date.getDate()
    const monthNames = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  }

  // Calculate total price for workshops
  const workshopsTotal = useMemo(() => {
    return workshops.reduce((sum, w) => sum + (w.prix || 0), 0)
  }, [workshops])

  if (!isSuperAdmin) return null

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Titre et période */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Dashboard Analytics</h1>
            <p className="text-gray-500 text-sm mt-1">Vue d'ensemble des projets et indicateurs financiers</p>
          </div>
          {analyticsView === 'tickets' ? (
            <div className="inline-flex flex-wrap items-center gap-1 self-start rounded-full bg-gray-100 p-1 sm:self-center">
              {TICKET_PERIODS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTicketPeriod(item.id)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    ticketPeriod === item.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {item.short}
                </button>
              ))}
            </div>
          ) : (
            <div className="inline-flex flex-wrap items-center gap-1 self-start rounded-full bg-gray-100 p-1 sm:self-center">
              <span className="inline-flex items-center gap-1.5 px-2.5 text-xs font-medium text-gray-500">
                <Calendar className="h-3.5 w-3.5" />
                Kickoff
              </span>
              <input
                type="date"
                aria-label="Date de début"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-8 rounded-full border-0 bg-white px-3 text-sm text-gray-800 shadow-sm outline-none focus:ring-2 focus:ring-cyan-200"
              />
              <span className="px-1 text-xs text-gray-400">au</span>
              <input
                type="date"
                aria-label="Date de fin"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-8 rounded-full border-0 bg-white px-3 text-sm text-gray-800 shadow-sm outline-none focus:ring-2 focus:ring-cyan-200"
              />
              {startDate || endDate ? (
                <button
                  type="button"
                  onClick={() => {
                    setStartDate('')
                    setEndDate('')
                  }}
                  className="rounded-full px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-800"
                >
                  Effacer
                </button>
              ) : null}
            </div>
          )}
        </div>

        <div className="mb-6 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setAnalyticsView('overview')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              analyticsView === 'overview' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Vue d'ensemble
          </button>
          <button
            type="button"
            onClick={() => setAnalyticsView('tickets')}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              analyticsView === 'tickets'
                ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Suivi des temps — Tickets
          </button>
        </div>

        {analyticsView === 'tickets' ? (
          <TicketTimeTracking period={ticketPeriod} />
        ) : loading ? (
          <PageSkeleton />
        ) : (
          <>
            {/* KPI cards - Projets et Workshops sur la même ligne */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
              {/* Total projets / Total workshops côte à côte */}
              <div className="rounded-xl p-4 shadow-sm" style={{ backgroundColor: '#1CB5C833' }}>
                <div className="text-sm font-medium text-gray-600">Total projets</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{projects.length}</div>
              </div>
              <div className="rounded-xl p-4 shadow-sm" style={{ backgroundColor: '#1CB5C833' }}>
                <div className="text-sm font-medium text-gray-600">Total workshops</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{workshops.length}</div>
              </div>
              
              {/* Projets KPIs */}
              <div className="rounded-xl p-4 bg-gray-50 border border-gray-200 shadow-sm">
                <div className="text-sm font-medium text-gray-600">Build moyen <span className="text-gray-400 font-normal">(Projets)</span></div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{fmtEuro(buildMoyen)}</div>
                <div className="text-xs text-gray-500 mt-1">Médiane: {fmtEuro(buildMediane)}</div>
              </div>
              <div className="rounded-xl p-4 bg-gray-50 border border-gray-200 shadow-sm">
                <div className="text-sm font-medium text-gray-600">Run moyen <span className="text-gray-400 font-normal">(Projets)</span></div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{fmtEuro(runMoyen)}</div>
                <div className="text-xs text-gray-500 mt-1">Médiane: {fmtEuro(runMediane)}</div>
              </div>
              
              {/* Workshops KPIs */}
              <div className="rounded-xl p-4 bg-gray-50 border border-gray-200 shadow-sm">
                <div className="text-sm font-medium text-gray-600">Prix moyen <span className="text-gray-400 font-normal">(Workshops)</span></div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{fmtEuro(prixMoyen)}</div>
                <div className="text-xs text-gray-500 mt-1">Médiane: {fmtEuro(prixMediane)}</div>
              </div>
              
              {/* Industries (commun) */}
              <div className="rounded-xl p-4 bg-gray-50 border border-gray-200 shadow-sm">
                <div className="text-sm font-medium text-gray-600">Industries</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{industries}</div>
              </div>
            </div>

            {/* Projets par industrie / par client */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="rounded-xl p-5 bg-white border border-gray-200 shadow-sm">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Projets par industrie</h2>
                <div className="space-y-3">
                  {byIndustry.map(({ nom, count }) => (
                    <div key={nom} className="flex items-center justify-between">
                      <span className="text-gray-700">{nom}</span>
                      <span className="px-3 py-1 rounded-full bg-[#E0F7FA] border border-cyan-200 text-[#00838F] text-sm font-medium">
                        {count} {count > 1 ? 'projets' : 'projet'}
                      </span>
                    </div>
                  ))}
                  {byIndustry.length === 0 && <span className="text-gray-500 text-sm">Aucune donnée</span>}
                </div>
              </div>
              <div className="rounded-xl p-5 bg-white border border-gray-200 shadow-sm">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Projets par client</h2>
                <div className="space-y-3">
                  {byClient.map(({ nom, count }) => (
                    <div key={nom} className="flex items-center justify-between">
                      <span className="text-gray-700">{nom}</span>
                      <span className="px-3 py-1 rounded-full bg-[#E0F7FA] border border-cyan-200 text-[#00838F] text-sm font-medium">
                        {count} {count > 1 ? 'projets' : 'projet'}
                      </span>
                    </div>
                  ))}
                  {byClient.length === 0 && <span className="text-gray-500 text-sm">Aucune donnée</span>}
                </div>
              </div>
            </div>

            {/* Workshops par industrie / par client */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="rounded-xl p-5 bg-white border border-gray-200 shadow-sm">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Workshops par industrie</h2>
                <div className="space-y-3">
                  {workshopsByIndustry.map(({ nom, count }) => (
                    <div key={nom} className="flex items-center justify-between">
                      <span className="text-gray-700">{nom}</span>
                      <span className="px-3 py-1 rounded-full bg-[#E0F7FA] border border-cyan-200 text-[#00838F] text-sm font-medium">
                        {count} {count > 1 ? 'workshops' : 'workshop'}
                      </span>
                    </div>
                  ))}
                  {workshopsByIndustry.length === 0 && <span className="text-gray-500 text-sm">Aucune donnée</span>}
                </div>
              </div>
              <div className="rounded-xl p-5 bg-white border border-gray-200 shadow-sm">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Workshops par client</h2>
                <div className="space-y-3">
                  {workshopsByClient.map(({ nom, count }) => (
                    <div key={nom} className="flex items-center justify-between">
                      <span className="text-gray-700">{nom}</span>
                      <span className="px-3 py-1 rounded-full bg-[#E0F7FA] border border-cyan-200 text-[#00838F] text-sm font-medium">
                        {count} {count > 1 ? 'workshops' : 'workshop'}
                      </span>
                    </div>
                  ))}
                  {workshopsByClient.length === 0 && <span className="text-gray-500 text-sm">Aucune donnée</span>}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="rounded-xl p-5 bg-white border border-gray-200 shadow-sm overflow-hidden">
              <h2 className="text-lg font-bold text-gray-800">Détail des projets</h2>
              <p className="text-sm text-gray-500 mb-4">Liste complète des projets sur la période sélectionnée</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-600 font-medium">
                      <th className="py-3 px-2">Projet</th>
                      <th className="py-3 px-2">Client</th>
                      <th className="py-3 px-2">Industrie</th>
                      <th className="py-3 px-2">Statut</th>
                      <th className="py-3 px-2">Build</th>
                      <th className="py-3 px-2">Run</th>
                      <th className="py-3 px-2">Coûts</th>
                      <th className="py-3 px-2">Marge</th>
                      <th className="py-3 px-2">Écart jours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p) => {
                      const mf = p.montant_facture_build
                      const co = p.couts
                      const margePct = (mf != null && co != null && mf > 0) ? ((mf - co) / mf * 100) : null
                      const ecart = (p.jours_reel != null && p.jours_vendu != null) ? (p.jours_reel - p.jours_vendu) : null
                      return (
                        <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <Link to="/projects/$projectId" params={{ projectId: String(p.id) }} className="text-cyan-600 hover:underline font-medium">
                              {p.nom}
                            </Link>
                          </td>
                          <td className="py-3 px-2 text-gray-700">{p.client_nom || '—'}</td>
                          <td className="py-3 px-2 text-gray-700">{p.activity_sector_nom || '—'}</td>
                          <td className="py-3 px-2">
                            <span className={`inline-flex px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(p.status)}`}>
                              {getStatusLabel(p.status)}
                            </span>
                          </td>
                          <td className="py-3 px-2">{mf != null ? fmtEuro(mf) : '—'}</td>
                          <td className="py-3 px-2">{p.montant_run != null ? fmtEuro(p.montant_run) : 'N/A'}</td>
                          <td className="py-3 px-2">{p.couts != null ? fmtEuro(p.couts) : '—'}</td>
                          <td className="py-3 px-2">
                            {margePct != null ? (
                              <span className="font-medium" style={{ color: '#D08700' }}>{margePct.toFixed(1)}%</span>
                            ) : '—'}
                          </td>
                          <td className="py-3 px-2">
                            {ecart != null ? (
                              <span className={`font-medium ${getEcartColor(ecart)}`}>
                                {ecart >= 0 ? '+' : ''}{ecart}j
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      )
                    })}
                    {/* Ligne TOTAL */}
                    {projects.length > 0 && (
                      <tr className="border-t-2 border-gray-200 font-bold" style={{ backgroundColor: '#1CB5C81A' }}>
                        <td className="py-3 px-2 text-gray-900">TOTAL</td>
                        <td className="py-3 px-2 text-gray-900"></td>
                        <td className="py-3 px-2 text-gray-900"></td>
                        <td className="py-3 px-2 text-gray-900"></td>
                        <td className="py-3 px-2 text-gray-900">{fmtEuro(totals.build)}</td>
                        <td className="py-3 px-2 text-gray-900">{totals.run > 0 ? fmtEuro(totals.run) : 'N/A'}</td>
                        <td className="py-3 px-2 text-gray-900">{fmtEuro(totals.couts)}</td>
                        <td className="py-3 px-2 text-gray-900">
                          {totals.marge != null ? (
                            <span style={{ color: '#D08700' }}>{totals.marge.toFixed(1)}%</span>
                          ) : '—'}
                        </td>
                        <td className="py-3 px-2 text-gray-900">
                          {totals.ecart != null ? (
                            <span className={getEcartColor(totals.ecart)}>
                              {totals.ecart >= 0 ? '+' : ''}{totals.ecart.toFixed(1)}j
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {projects.length === 0 && (
                <div className="py-12 text-center text-gray-500">Aucun projet</div>
              )}
            </div>

            {/* Workshops Table */}
            <div className="rounded-xl p-5 bg-white border border-gray-200 shadow-sm overflow-hidden mt-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                <h2 className="text-lg font-bold text-gray-800">Workshops</h2>
              </div>
              <p className="text-sm text-gray-500 mb-4">Liste des workshops sur la période sélectionnée</p>
              {loadingWorkshops ? (
                <PageSkeleton variant="table" />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-left text-gray-600 font-medium">
                          <th className="py-3 px-2">Client</th>
                          <th className="py-3 px-2">Industrie</th>
                          <th className="py-3 px-2">Consultants</th>
                          <th className="py-3 px-2">Date</th>
                          <th className="py-3 px-2">Prix</th>
                          <th className="py-3 px-2">Liens</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workshops.map((w) => {
                          const consultants = w.consultants ? w.consultants.split(',').map(c => c.trim()) : []
                          const figmaUrls = w.urls?.filter(u => u.url_type === 'figma') || []
                          const propositionUrls = w.urls?.filter(u => u.url_type === 'proposition') || []
                          
                          return (
                            <tr key={w.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-2 text-gray-700">{w.client_nom || '—'}</td>
                              <td className="py-3 px-2 text-gray-700">{w.activity_sector_nom || '—'}</td>
                              <td className="py-3 px-2 text-gray-700">
                                {consultants.length > 0 ? consultants.join(', ') : '—'}
                              </td>
                              <td className="py-3 px-2 text-gray-700">{formatWorkshopDate(w.date)}</td>
                              <td className="py-3 px-2 text-gray-700">
                                {w.prix != null ? fmtEuro(w.prix) : '—'}
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {figmaUrls.length > 0 && (
                                    <a
                                      href={figmaUrls[0]!.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-cyan-50 text-cyan-600 text-xs font-medium border border-cyan-200 hover:bg-cyan-100 transition"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      Figma
                                    </a>
                                  )}
                                  {propositionUrls.length > 0 && (
                                    <a
                                      href={propositionUrls[0]!.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-green-50 text-green-600 text-xs font-medium border border-green-200 hover:bg-green-100 transition"
                                    >
                                      <FileText className="w-3 h-3" />
                                      Proposition
                                    </a>
                                  )}
                                  {figmaUrls.length === 0 && propositionUrls.length === 0 && '—'}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                        {/* Ligne TOTAL */}
                        {workshops.length > 0 && (
                          <tr className="border-t-2 border-gray-200 font-bold" style={{ backgroundColor: '#1CB5C81A' }}>
                            <td className="py-3 px-2 text-gray-900">TOTAL</td>
                            <td className="py-3 px-2 text-gray-900"></td>
                            <td className="py-3 px-2 text-gray-900"></td>
                            <td className="py-3 px-2 text-gray-900"></td>
                            <td className="py-3 px-2 text-gray-900">{fmtEuro(workshopsTotal)}</td>
                            <td className="py-3 px-2 text-gray-900"></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {workshops.length === 0 && (
                    <div className="py-12 text-center text-gray-500">Aucun workshop</div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  )
}
